/** The error envelope every AGOS endpoint answers with (BACKEND.md §8). */
export interface ApiErrorEnvelope {
  error: string
  code?: string
}

function readStringField(value: unknown, key: string): string | undefined {
  if (!value || typeof value !== 'object') return undefined
  const candidate = (value as Record<string, unknown>)[key]
  if (typeof candidate !== 'string') return undefined
  const trimmed = candidate.trim()
  return trimmed === '' ? undefined : trimmed
}

function truncate(text: string, maxLength = 300): string {
  return text.length > maxLength ? `${text.slice(0, maxLength - 1)}…` : text
}

function buildDeveloperMessage(response: Response, data: unknown): string {
  const prefix = `HTTP ${response.status} ${response.statusText}`
  if (typeof data === 'string') {
    const text = data.trim()
    return text ? `${prefix}: ${truncate(text)}` : prefix
  }
  const envelopeMessage = readStringField(data, 'error') ?? readStringField(data, 'message')
  return envelopeMessage ? `${prefix}: ${envelopeMessage}` : prefix
}

export class ApiError<T = unknown> extends Error {
  override readonly name = 'ApiError'
  readonly status: number
  readonly statusText: string
  readonly data: T | null
  readonly headers: Headers
  readonly response: Response
  readonly method: string
  readonly url: string

  constructor(response: Response, data: T | null, request: { method: string; url: string }) {
    super(buildDeveloperMessage(response, data))
    Object.setPrototypeOf(this, new.target.prototype)
    this.status = response.status
    this.statusText = response.statusText
    this.data = data
    this.headers = response.headers
    this.response = response
    this.method = request.method
    this.url = response.url || request.url
  }

  /** The `code` discriminator, when the backend sent one. */
  get code(): string | undefined {
    return readStringField(this.data, 'code')
  }
}

export class ResponseParseError extends Error {
  override readonly name = 'ResponseParseError'
  readonly status: number
  readonly response: Response
  readonly method: string
  readonly url: string
  readonly rawBody: string
  override readonly cause: unknown

  constructor(
    response: Response,
    rawBody: string,
    cause: unknown,
    request: { method: string; url: string },
  ) {
    super(
      `Failed to parse the response to ${request.method} ${response.url || request.url} ` +
        `(${response.status} ${response.statusText}) as JSON`,
    )
    Object.setPrototypeOf(this, new.target.prototype)
    this.status = response.status
    this.response = response
    this.method = request.method
    this.url = response.url || request.url
    this.rawBody = rawBody
    this.cause = cause
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError
}

/**
 * The string to put in front of a user.
 *
 * The backend's Slovak message is shown as-is — FRONTEND.md §6 — with no HTTP
 * status glued to the front and no rewording. `fallback` covers the cases the
 * envelope cannot: a network drop, or a proxy answering HTML.
 */
export function apiErrorMessage(error: unknown, fallback: string): string {
  if (isApiError(error)) {
    return readStringField(error.data, 'error') ?? fallback
  }
  return fallback
}

/**
 * Any string the error envelope carries, by name.
 *
 * `apiErrorMessage` covers the one field almost every failure has. Some carry another the screen needs:
 * a 429 from `/mini-game/play` sends `nextPlayAt` **so that the screen can count down to it**, and a
 * refusal without it leaves the customer counting down to nothing.
 *
 * Here rather than in a consumer because `ApiError` is here, and six copies of this read the wrong field
 * once already — FB-C40. One place to be wrong.
 */
export function apiErrorField(error: unknown, field: string): string | null {
  if (isApiError(error)) return readStringField(error.data, field) ?? null
  // A hand-thrown object in a test, or a shape from before this client — read it rather than go quiet.
  if (typeof error === 'object' && error !== null) {
    const loose = error as { data?: unknown; body?: unknown }
    return readStringField(loose.data, field) ?? readStringField(loose.body, field) ?? null
  }
  return null
}

/**
 * Whether the request reached the server at all.
 *
 * A connection that never arrived and a request the server refused are different things to somebody
 * looking at the screen: one is *try again*, the other is *this will not work however many times you
 * try*.
 */
/**
 * Whether the server refused this for want of a right, rather than for anything the screen sent.
 *
 * **§9.341.** A 403 and a 400 want different sentences: a 400 usually describes the request, and a
 * screen can repeat the server's own words; a 403 describes the *person*, and the server's wording
 * for that is written for a developer - `OrderUpdateService`'s is *"Použite /confirm-status"*, which
 * a dispatcher was shown in a toast. A screen that can tell the two apart can answer *who may do
 * this* instead.
 *
 * Here rather than in a consumer for the same reason as `apiErrorField`: `ApiError` is here, and one
 * place to be wrong is the point.
 */
export function isForbidden(error: unknown): boolean {
  return isApiError(error) && error.status === 403
}

/**
 * Whether the server answered *there is no such thing* - §3.162, and §9.544's rule given one home.
 *
 * **A 404 is an ANSWER and everything else is this end failing**, which is §9.100's sentence quoted in
 * `customer-cms`: *"Only the server saying 404 means missing or unpublished; anything else is this end
 * failing and should say so."* `customFetch`'s `await fetch(...)` is not wrapped, so a dropped
 * connection, a DNS failure, a CORS failure or an abort rejects with a **`TypeError`**, and
 * `ResponseParseError extends Error` rather than `ApiError` - so neither is a 404 and neither means
 * missing.
 *
 * **Here because the rule was spelled once and needed twice.** §9.544 extracted `pageIsMissing` into
 * `@agos/customer-cms` after finding the inline version **inverted**; §3.154 then split the marketing
 * site's own two states and branched on react-query's raw `isError`, **which is true for a 404** - so a
 * page that really is unpublished read as *we could not load this, try again*, the inverse of the defect
 * §3.154 fixed. Two public renderers of one concept, and neither could see the other:
 * `@agos/marketing` cannot import a customer package. `ApiError` lives here, so the rule does too -
 * `isForbidden`'s own reason, one line up.
 */
export function isNotFound(error: unknown): boolean {
  return isApiError(error) && error.status === 404
}

/**
 * Whether another attempt could change the answer - FB-834, §9.548.
 *
 * **Every 4xx except 408 and 429 is final.** Retrying a 400, a 401, a 403, a 404, a 409 or a 422 cannot change what
 * the server said, so a retry only delays the sentence the screen is going to show anyway. A **429** is a limit that
 * lifts - the customer app's rate limiter is keyed per address (§10A.255), so a phone behind a carrier's NAT takes one
 * for somebody else's traffic - and a **408** is a timeout. A 5xx, a dropped connection and a body that will not parse
 * are all worth another go.
 *
 * **Here rather than in a consumer for the same reason as `isForbidden` and `apiErrorField`**: `ApiError` is here, and
 * one place to be wrong is the point. §3.158 shipped a local copy of this rule in `apps/marketing` an hour before
 * §9.548 needed a second, which is `agos-one-guard-spelled-twice` on a predicate.
 *
 * **Stated positively on purpose.** FB-829 was one inverted operator inside the code written to prevent that class, and
 * a predicate named for the thing you must *not* do reads as a double negative at the call site:
 * `retry: (attempt, error) => attempt < 1 && worthRetrying(error)` says the policy.
 */
export function worthRetrying(error: unknown): boolean {
  if (!isApiError(error)) {
    // A network failure, an aborted request or a `ResponseParseError` - none of which is the server's answer.
    return true
  }
  if (error.status === 408 || error.status === 429) {
    return true
  }
  return error.status < 400 || error.status >= 500
}

/**
 * The retry policy every app uses - FB-835, §9.549 and §3.159.
 *
 * **Two attempts, and the count belongs here with the predicate.** §9.548 put `worthRetrying` here and left the
 * attempt count spelled at each call site, which made the policy two decisions in two places; §3.158 had already
 * spelled one of them in `apps/marketing` and FB-835's sweep found **twelve** local `retry: false` across the tree. A
 * policy copied that many times is `agos-one-guard-spelled-twice` on the rule this file exists to hold once.
 *
 * **Two rather than react-query's three**, because the second attempt is what rides out a blip and the third only
 * makes the visitor wait: it lands after roughly four seconds of nothing, by which point the screen's own failure
 * sentence and its retry button are the better answer. §3.158 chose that count for the shopfront, and legacy's own
 * super-admin is `retry: 1` - a coincidence worth recording rather than an authority, since legacy never made this
 * decision per read.
 *
 * **A plain function rather than react-query's `RetryValue` type**, so this module stays about errors: the signature
 * is what `defaultOptions.queries.retry` and a per-call `query.retry` both accept.
 */
export function retryTransient(attempt: number, error: unknown): boolean {
  return attempt < 1 && worthRetrying(error)
}

export function reachedServer(error: unknown): boolean {
  return isApiError(error) || (typeof error === 'object' && error !== null && 'response' in error)
}
